/**
 * apiClient — the single fetch wrapper used by every TanStack Query hook.
 *
 * Responsibilities:
 *   - Prepend `VITE_API_URL` to every path.
 *   - Attach `Authorization: Bearer <token>` from the cookie helpers.
 *   - Attach `X-Tenant-Subdomain` derived from `window.location.host`.
 *   - Parse JSON, throw a typed `ApiError` on non-2xx.
 *   - On 401: silently refresh the Cognito session once and replay the
 *     request; only clear cookies (→ TanStack Query callers redirect to
 *     `/login`) when the refresh fails or the replay still 401s.
 */

import { refreshSession } from '@/auth/cognito'
import {
  clearTokens,
  getAccessToken as getAccessTokenCookie,
  getIdToken as getIdTokenCookie,
} from '@/auth/cookie-helpers'
import { parseTenantSubdomain } from '@/auth/host-parser'
import { hasStorageWentOverLimit, notifyStorageWentOverLimit } from './storage-limit-signal'

const API_BASE_URL = import.meta.env.VITE_API_URL ?? ''

/**
 * Browser-resolved IANA timezone name (e.g. `America/Los_Angeles`).
 * Sent on every authenticated request as `X-User-Timezone` so the BE can
 * bias time-entry math (legacy 3mote-fe parity — the legacy axios
 * interceptor attaches the same header). Cached at module scope; the
 * resolved zone never changes mid-session.
 */
const USER_TIMEZONE: string = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
})()

/**
 * Read the active UI locale from `<html lang>`. The pre-paint script in
 * `index.html` and the `useLocale` hook keep this in sync with
 * localStorage + the BE-persisted value. Reading from the DOM (not
 * localStorage) lets a request-time locale switch take effect without a
 * full page reload — the next request after `setLocale` carries the new
 * header.
 *
 * Sent as `X-User-Locale` on every authenticated request — the BE's
 * `resolveLocale(c)` reads it as precedence-1 over the user/tenant DB
 * columns, mirroring the FE's i18next active language so server-rendered
 * email / error templates speak the same language as the UI.
 */
const readLocale = (): string => {
  if (typeof document === 'undefined') return 'en'
  const lang = document.documentElement.getAttribute('lang')
  return lang && lang.length > 0 ? lang : 'en'
}

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(message: string, status: number, body: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

export interface ApiClientOptions extends Omit<RequestInit, 'body'> {
  /** JSON-serializable body. Strings and FormData are passed through unchanged. */
  body?: unknown
  /** Skip the Authorization header (e.g. for unauth signup/login routes). */
  skipAuth?: boolean
  /** Override the tenant subdomain (mainly for tests). */
  tenantSubdomain?: string
  /**
   * Override the auto-injected `Idempotency-Key` header. Wiki mutation
   * routes (POST/PATCH/PUT/DELETE on `/api/wiki/*`) get an Idempotency-Key
   * auto-stamped from `crypto.randomUUID()` so a retried PUT (browser
   * refresh, network blip, optimistic-rollback retry) is deduplicated
   * server-side via the `wiki_idempotency` table. Callers can pin a
   * specific key here when they need to coordinate (e.g. a Wave 2
   * mutation hook that wants to retry the SAME logical action and reach
   * the cached BE response). Non-wiki routes ignore this option.
   */
  idempotencyKey?: string
  /**
   * Override the `If-Match` header used for optimistic concurrency on
   * wiki saves. The FE sends the editor's known `draft_version_id`;
   * the BE returns 409 `wiki_draft_stale` if the version on disk has
   * advanced. Optional — only wiki save paths set this. Mirrored on the
   * options interface (instead of relying on `headers`) so the hook
   * surface stays declarative.
   */
  ifMatch?: string
}

/**
 * Reads the current access token from the `.3mote.com` cookie. This is
 * what the API client attaches to every request. It is the cookie — not
 * `localStorage` — because the cookie is shared across subdomains.
 */
export function getAccessToken(): string | null {
  return getAccessTokenCookie()
}

function resolveTenantSubdomain(override?: string): string {
  if (override !== undefined) return override
  if (typeof window === 'undefined') return 'aeeiee'
  try {
    return parseTenantSubdomain(window.location.host)
  } catch {
    // Unknown host (e.g. CI preview) — fall back to the dev tenant rather
    // than crashing the SPA. The BE authorizer will reject it anyway if
    // it's wrong.
    return 'aeeiee'
  }
}

/**
 * Wiki mutation paths get auto-injected `Idempotency-Key` + (when the
 * caller supplies it) `If-Match` headers. The scope is intentionally
 * narrow — every other domain has its own retry semantics and shouldn't
 * pay the per-request UUID cost. The check matches:
 *
 *   - method ∈ {POST, PATCH, PUT, DELETE}
 *   - path starts with `/api/wiki/`
 *
 * Why: the wiki BE keys idempotency on
 * `(tenant_id, actor_user_id, endpoint_path, idempotency_key)` per plan
 * §3.3, and uses `If-Match: <draft_version_id>` for optimistic
 * concurrency on every save. Without the Idempotency-Key, a network
 * blip on Publish (the 409 `wiki_publish_conflict` path) could produce
 * a duplicate version row when the FE retries; with it, the second
 * attempt hits the cached response (200 + same payload) and the user
 * sees one publish, not two.
 */
const WIKI_MUTATION_PATH = /^\/api\/wiki\//
const MUTATION_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE'])

function isWikiMutation(path: string, method: string | undefined): boolean {
  if (!path) return false
  // Strip the API base URL (if any) and any leading querystring so the
  // regex sees the canonical `/api/wiki/...` prefix even when callers
  // pass an absolute URL or a path-with-search.
  const url = path.startsWith('http')
    ? (() => {
        try {
          return new URL(path).pathname
        } catch {
          return path
        }
      })()
    : path
  const m = (method ?? 'GET').toUpperCase()
  return MUTATION_METHODS.has(m) && WIKI_MUTATION_PATH.test(url)
}

/**
 * Generate a fresh idempotency key. Uses `crypto.randomUUID()` (RFC
 * 4122 v4, monotonic-ish across the same browsing context) where
 * available, falling back to a Math.random-based 32-char hex on older
 * browsers. The key only needs to be unique per
 * (tenant, user, endpoint, request body hash) — collisions across
 * different users in the same browser tab are impossible by virtue of
 * being keyed on `actor_user_id` too.
 */
function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback: 16 random bytes hex-encoded. Not cryptographically
  // distinct from the dev-pool the UUIDv4 generator covers; just
  // enough entropy that two mutations from the same hook never
  // collide.
  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

function buildHeaders(options: ApiClientOptions, path: string): Headers {
  const headers = new Headers(options.headers ?? {})
  const tenant = resolveTenantSubdomain(options.tenantSubdomain)
  if (tenant) headers.set('X-Tenant-Subdomain', tenant)
  // Every request announces the caller's IANA timezone (e.g.
  // `America/New_York`) so the BE can bias time-entry math without
  // round-tripping for it. The legacy axios interceptor sent
  // `X-User-Timezone`; we send `X-Timezone` to match the deployed API
  // Gateway CORS allowlist (CDK source `3mote-infra-new/lib/api-stack.ts`).
  // The header NAME isn't load-bearing — semantics carry; if/when the
  // CDK is updated to accept `X-User-Timezone` we can switch (or send
  // both). The Hono middleware in `3mote-be-new/src/index.ts` accepts
  // both names already.
  if (!headers.has('X-Timezone')) headers.set('X-Timezone', USER_TIMEZONE)
  // i18n header — every request announces the caller's active UI locale
  // so server-rendered text (validation errors, email templates queued
  // off the request, etc.) can match the UI's language without a
  // round-trip to /api/me. The BE's `resolveLocale(c)` reads this as
  // precedence-1; it's optional, so anonymous routes still work without
  // it. The CDK CORS allowlist on API Gateway accepts this header — keep
  // it in sync with `3mote-be-new/src/index.ts`.
  if (!headers.has('X-User-Locale')) headers.set('X-User-Locale', readLocale())

  if (!options.skipAuth) {
    const token = getAccessToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    // Cognito access tokens don't carry email/name claims by default.
    // The bootstrap route reads them from the ID token via this header.
    // Attaching it on every authenticated request is harmless.
    const idToken = getIdTokenCookie()
    if (idToken) headers.set('X-Id-Token', idToken)
  }

  // Default content type for JSON bodies (skipped for FormData / strings).
  if (
    options.body !== undefined &&
    !(options.body instanceof FormData) &&
    typeof options.body !== 'string' &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json')
  }

  // Wiki-only header injection. Scoped narrowly so other domains don't
  // accidentally start sending Idempotency-Key (which the BE would
  // currently ignore but would surface in CORS preflights). See the
  // comment on `isWikiMutation` above for the why.
  if (isWikiMutation(path, options.method)) {
    if (!headers.has('Idempotency-Key')) {
      headers.set('Idempotency-Key', options.idempotencyKey ?? generateIdempotencyKey())
    }
    if (options.ifMatch && !headers.has('If-Match')) {
      headers.set('If-Match', options.ifMatch)
    }
  }
  return headers
}

function serializeBody(body: unknown): BodyInit | undefined {
  if (body === undefined || body === null) return undefined
  if (body instanceof FormData) return body
  if (typeof body === 'string') return body
  return JSON.stringify(body)
}

/**
 * Module-scope single-flight guard for the silent token refresh. When a
 * burst of requests 401s at once (the common case — every TanStack Query
 * on a page fires together once the 1-hour access token lapses), they all
 * await the SAME `refreshSession()` call instead of each minting a new
 * one. `refreshSession()` writes the new tokens to the `.3mote.com`
 * cookies, so the replayed requests pick up the fresh access token.
 */
let inflightRefresh: Promise<void> | null = null

async function attemptTokenRefresh(): Promise<boolean> {
  if (!inflightRefresh) {
    inflightRefresh = refreshSession()
      .then(() => undefined)
      .finally(() => {
        inflightRefresh = null
      })
  }
  const pending = inflightRefresh
  try {
    await pending
    return true
  } catch {
    return false
  }
}

/**
 * Make a request. Returns parsed JSON, or `undefined` for 204 responses.
 * Throws `ApiError` for non-2xx, with the parsed body attached when JSON.
 *
 * On 401 we clear cookies before throwing so the next render of the
 * authenticated home page redirects to `/login`. Callers catching 401
 * can simply rethrow.
 */
export async function apiClient<T = unknown>(
  path: string,
  options: ApiClientOptions = {},
): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`
  const {
    body,
    skipAuth,
    tenantSubdomain: _tenant,
    idempotencyKey: _idempotencyKey,
    ifMatch: _ifMatch,
    ...rest
  } = options

  // Headers are rebuilt per attempt so the replay after a refresh reads the
  // freshly-rotated access token from the cookie (buildHeaders calls
  // getAccessToken() each time).
  const doFetch = (): Promise<Response> =>
    fetch(url, { ...rest, headers: buildHeaders(options, path), body: serializeBody(body) })

  let res = await doFetch()

  // Silent refresh-and-retry on the FIRST 401. An expired 1-hour access
  // token is the common case; refresh against Cognito's 30-day refresh
  // token and replay the request once so the user stays signed in. Only a
  // failed refresh OR a second 401 is treated as a dead session (handled by
  // the clearTokens() in the !res.ok block below → route guards bounce to
  // /login). skipAuth routes (login / signup) never refresh — a 401 there
  // is a genuine credential failure, not an expired token.
  if (res.status === 401 && !skipAuth && (await attemptTokenRefresh())) {
    res = await doFetch()
  }

  if (res.status === 204) {
    return undefined as T
  }

  const text = await res.text()
  const contentType = res.headers.get('content-type') ?? ''
  const parsed: unknown = contentType.includes('application/json') && text ? JSON.parse(text) : text

  if (!res.ok) {
    if (res.status === 401) {
      clearTokens()
    }
    // BE wire shape is `{ error: { code, message } }` (see
    // `3mote-be-new/src/lib/errors.ts`). Walk that first; fall back to a
    // top-level `{ message }` (some Cognito SDK errors surface that way),
    // then a synthesized "Request failed: N" string. R-2 caught that the
    // common case was producing a useless "Request failed: 403" toast.
    const extractMessage = (p: unknown): string | null => {
      if (typeof p !== 'object' || p === null) return null
      const obj = p as Record<string, unknown>
      const errBlock = obj.error
      if (typeof errBlock === 'object' && errBlock !== null && 'message' in errBlock) {
        const m = (errBlock as { message: unknown }).message
        if (typeof m === 'string' && m) return m
      }
      if (typeof obj.message === 'string' && obj.message) return obj.message
      return null
    }
    const message = extractMessage(parsed) ?? `Request failed: ${res.status} ${res.statusText}`
    throw new ApiError(message, res.status, parsed)
  }

  // A write that took the workspace over its storage limit says so in a
  // top-level `meta` block. Surface it once, app-wide, without changing
  // what the caller receives.
  if (hasStorageWentOverLimit(parsed)) notifyStorageWentOverLimit()

  return parsed as T
}

// Re-export so tests and callers can keep a single import path.
export { parseTenantSubdomain }
